// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useState } from 'react';

import {
  type DateOnly,
  type DateRange,
  formatDate,
  formatDateRange,
  today,
  type TimeFrameOption,
  timeFrameOptions,
  type TimeFrameData,
} from '@evolvemep/api-ts';
import { Box, type MantineSize } from '@mantine/core';
import { DatePicker } from '@mantine/dates';

import { WrappedPopover } from 'components/Mantine/Popovers/WrappedPopover';
import type { TypeSafeComboboxItem, TypeSafeGroupComboboxData } from 'components/Mantine/TypeSafeSelect/types';
import { WrappedSelect } from 'components/Mantine/TypeSafeSelect/WrappedSelect';
import { isNil, isNotNil } from 'helpers/isNotNil';

export type TimeFrameGroup = {
  group: string;
  disabled?: boolean;
  items: readonly TimeFrameOption[];
};

export type TimeFramePickerProps = {
  label?: string;
  value: TimeFrameData;
  onChange: (data: TimeFrameData) => void;
  size?: MantineSize;
  disabled?: boolean;
  minDate?: DateOnly;
  maxDate?: DateOnly | null;
  withinPortal?: boolean;
  timeFrameOptions?: readonly TimeFrameOption[];
  /** If provided, will override `timeFrameOptions` */
  timeFrameGroups?: readonly TimeFrameGroup[];
};

const formatTimestamp = (timestamp: Date | DateOnly | null) => formatDate(timestamp, 'MM/dd/yy', '');
const getTimeFrameLabel = (dateOrRange: Date | DateOnly | DateRange | [Date | null, Date | null] | null) => {
  if (isNil(dateOrRange)) return null;
  if (Array.isArray(dateOrRange)) {
    if (!dateOrRange.some(isNotNil)) return null;
    return formatDateRange(dateOrRange);
  }
  return formatTimestamp(dateOrRange);
};

export const TimeFramePicker = ({
  label,
  value,
  onChange,
  disabled,
  size,
  minDate,
  maxDate = today(),
  timeFrameOptions: options = timeFrameOptions,
  timeFrameGroups,
  withinPortal,
}: TimeFramePickerProps) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [internalTimeFrame, setInternalTimeFrame] = useState<TimeFrameData['timeFrame']>();
  const [internalDate, setInternalDate] = useState<DateOnly | null>(null);
  const [internalDateRange, setInternalDateRange] = useState<DateRange | null>(null);
  const [dropdownOpened, setDropdownOpened] = useState(false);

  const formattedOptions = useMemo(() => {
    const formatOption = (option: TimeFrameOption, optionDisabled?: boolean) => {
      if (!dropdownOpened) {
        if (option === 'Date Range') {
          return {
            value: option,
            label: getTimeFrameLabel(internalDateRange ?? value.dateRange) ?? option,
            disabled: optionDisabled,
          };
        }
        if (option === 'Specific Date') {
          return {
            value: option,
            label: getTimeFrameLabel(internalDate ?? value.date) ?? option,
            disabled: optionDisabled,
          };
        }
      }
      return { value: option, label: option, disabled: optionDisabled };
    };
    if (isNotNil(timeFrameGroups)) {
      return timeFrameGroups.map(({ group, items: groupOptions, disabled: groupDisabled }) => ({
        group,
        items: groupOptions.map((groupOption) => formatOption(groupOption, groupDisabled)),
      })) as TypeSafeGroupComboboxData<TimeFrameOption, unknown>[];
    }
    return options.map((option) => formatOption(option)) as TypeSafeComboboxItem<TimeFrameOption, unknown>[];
  }, [timeFrameGroups, options, dropdownOpened, internalDateRange, value.dateRange, value.date, internalDate]);

  return (
    <WrappedPopover
      opened={showDatePicker}
      onConfirm={() => {
        if (internalTimeFrame === 'Date Range') {
          onChange({
            timeFrame: internalTimeFrame,
            dateRange: internalDateRange ?? [null, null],
            date: null,
          });
        } else if (internalTimeFrame === 'Specific Date') {
          onChange({
            timeFrame: internalTimeFrame,
            date: internalDate,
            dateRange: null,
          });
        } else if (isNotNil(internalTimeFrame)) {
          onChange({
            timeFrame: internalTimeFrame,
            date: null,
            dateRange: null,
          });
        }
        // The auto-close `onClose` below resets the internal state; the labels fall back
        // to the `value` just committed via `onChange`.
      }}
      onClose={() => {
        setShowDatePicker(false);
        setInternalTimeFrame(undefined);
        setInternalDate(null);
        setInternalDateRange(null);
      }}
      withCloseButton={false}
      isDirty
      withinPortal={withinPortal}
      confirmationText="Done"
      target={
        <Box>
          <WrappedSelect
            label={label}
            size={size}
            value={internalTimeFrame ?? value.timeFrame}
            data={formattedOptions}
            onChange={(newTimeFrame) => {
              if (newTimeFrame === 'Specific Date' || newTimeFrame === 'Date Range') {
                setInternalDate(value.date ?? today());
                setInternalDateRange(value.dateRange);
                setInternalTimeFrame(newTimeFrame);
                setShowDatePicker(true);
              } else {
                setInternalTimeFrame(undefined);
                onChange({
                  timeFrame: newTimeFrame,
                  date: null,
                  dateRange: null,
                });
              }
            }}
            onDropdownOpen={() => setDropdownOpened(true)}
            onDropdownClose={() => setDropdownOpened(false)}
            maxDropdownHeight={400}
            disabled={disabled}
            withinPortal={withinPortal}
            placeholder="Time Frame"
          />
        </Box>
      }
      disabled={internalTimeFrame === 'Date Range' ? internalDateRange?.includes(null) : internalDate === null}
    >
      {internalTimeFrame === 'Date Range' ? (
        <DatePicker
          type="range"
          allowSingleDateInRange
          value={internalDateRange ?? [null, null]}
          onChange={(v) => setInternalDateRange(v as DateRange)}
          defaultDate={internalDateRange?.[0] ?? today()}
          maxDate={maxDate ?? undefined}
          minDate={minDate}
        />
      ) : (
        <DatePicker
          type="default"
          value={internalDate}
          onChange={(v) => setInternalDate(v as DateOnly)}
          defaultDate={internalDate ?? today()}
          maxDate={maxDate ?? undefined}
          minDate={minDate}
        />
      )}
    </WrappedPopover>
  );
};
